import React, { useState } from "react";import { Menu, X, Phone, Mail, MapPin, Clock, Instagram, Facebook, ChevronRight, Sparkles, ShieldCheck, Wrench, Droplet, Car, Check, MessageSquare } from "lucide-react";
import { Toaster, toast } from "sonner";
import axios from "axios";
import { SITE, SERVICES, PACKAGES, SERVICE_AREAS, QUOTE_SMS_HREF } from "./lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fire a Meta Pixel event if the pixel is loaded — safe no-op otherwise.
const trackFb = (event, params) => {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    try { window.fbq("track", event, params || {}); } catch (_) { /* noop */ }
  }
};

// ---------- HEADER ----------
const NAV = [
  { label: "Services", href: "#services" },
  { label: "Packages", href: "#packages" },
  { label: "Gallery", href: "#gallery" },
  { label: "Area", href: "#area" },
  { label: "Contact", href: "#contact" },
];

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <a href="#top" data-testid="logo-link" className="flex items-center group">
          <img src={SITE.logo} alt="Legacy Auto — Kansas City Mobile Detailing" className="h-14 w-auto object-contain" />
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="text-sm uppercase tracking-widest text-neutral-300 hover:text-white transition-colors"
            >
              {n.label}
            </a>
          ))}
        </nav>

          <a
            href={SITE.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-book-button"
            onClick={() => trackFb("Lead", { content_name: "Square Booking — Header" })}
            className="hidden md:inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-semibold uppercase tracking-widest text-xs px-5 py-3 transition-all hover:scale-[1.03]"
          >
            Book Now <ChevronRight className="w-4 h-4" />
          </a>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden border-t border-white/5 bg-black/95">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className="text-base uppercase tracking-widest text-neutral-200"
              >
                {n.label}
              </a>
            ))}
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-book-button"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#E60000] text-white font-semibold uppercase tracking-widest text-xs px-5 py-4"
            >
              Book Now <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

// ---------- HERO ----------
function Hero() {
  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/31298995/pexels-photo-31298995.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600')",
        }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#050505]" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/30 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-28 pb-32 lg:pt-40 lg:pb-48">
        <div className="inline-flex items-center gap-2 border border-[#E60000]/50 text-[#E60000] uppercase text-xs tracking-[0.3em] px-3 py-1 mb-8">
          <span className="w-1.5 h-1.5 bg-[#E60000] rounded-full animate-pulse" />
          Now Booking · Kansas City Metro
        </div>

        <h1
          data-testid="hero-title"
          className="font-display uppercase text-6xl sm:text-7xl lg:text-8xl xl:text-9xl leading-[0.85] tracking-tight max-w-5xl"
        >
          We bring the<br />
          <span className="text-[#E60000]">detail shop</span><br />
          to your driveway.
        </h1>

        <p className="mt-8 text-base sm:text-lg text-neutral-300 max-w-2xl leading-relaxed">
          Legacy Auto is a premium mobile detailing studio serving the Kansas City metro.
          From maintenance washes to multi-stage paint correction and ceramic coatings —
          we come to you with water, power and a relentless eye for finish.
        </p>

        <div className="mt-10 flex flex-wrap gap-4">
          <a
            href={SITE.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-book-button"
            onClick={() => trackFb("Lead", { content_name: "Square Booking — Hero" })}
            className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-semibold uppercase tracking-widest text-sm px-8 py-4 transition-all hover:scale-[1.03]"
          >
            Book on Square <ChevronRight className="w-4 h-4" />
          </a>
          <a
            href={QUOTE_SMS_HREF}
            data-testid="hero-sms-quote-button"
            onClick={() => trackFb("Lead", { content_name: "SMS Quote — Hero" })}
            className="inline-flex items-center gap-2 border border-white/20 hover:border-[#E60000] hover:text-[#E60000] text-white uppercase tracking-widest text-sm px-8 py-4 transition-colors"
          >
            <MessageSquare className="w-4 h-4" /> Text for a Quote
          </a>
          <a
            href="#services"
            data-testid="hero-services-button"
            className="inline-flex items-center gap-2 border border-white/20 hover:border-white/60 text-white uppercase tracking-widest text-sm px-8 py-4 transition-colors"
          >
            Explore Services
          </a>
        </div>

        <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
          {[
            { k: "5.0", v: "Avg Google Rating" },
            { k: "12+", v: "KC Suburbs Served" },
            { k: "100%", v: "Mobile Service" },
          ].map((s) => (
            <div key={s.v} data-testid={`stat-${s.v.replace(/\s+/g, '-').toLowerCase()}`} className="border-l border-[#E60000]/50 pl-4">
              <div className="font-display text-3xl text-white">{s.k}</div>
              <div className="text-xs uppercase tracking-widest text-neutral-400 mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- MARQUEE ----------
function Marquee() {
  const items = ["Mobile Detailing", "Kansas City", "Ceramic Coating", "Paint Correction", "Interior Steam", "Legacy Auto"];
  const row = (
    <div className="flex shrink-0 items-center gap-12 px-6">
      {items.map((t, i) => (
        <span key={i} className="font-display text-7xl md:text-8xl uppercase text-stroke whitespace-nowrap">
          {t} <span className="text-[#E60000]">/</span>
        </span>
      ))}
    </div>
  );
  return (
    <div data-testid="marquee-band" className="relative border-y border-white/5 py-8 overflow-hidden bg-[#0A0A0A]">
      <div className="flex animate-marquee w-max">
        {row}{row}
      </div>
    </div>
  );
}

// ---------- SERVICES ----------
const SERVICE_ICONS = {
  standard: Car,
  premium: Sparkles,
  interior: Droplet,
  exterior: Wrench,
  ceramic: ShieldCheck,
};

function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#E60000] mb-4">— What we do</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight max-w-2xl">
              Services built<br/>for the long haul.
            </h2>
          </div>
          <p className="text-neutral-400 max-w-md text-base leading-relaxed">
            Every detail is performed at your home or office with professional gear,
            water reclaim mats and finishing standards borrowed from the show car world.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 auto-rows-[280px]">
          {SERVICES.map((s) => {
            const Icon = SERVICE_ICONS[s.key] || Car;
            return (
              <div
                key={s.key}
                data-testid={`service-card-${s.key}`}
                className={`group relative overflow-hidden border border-[#262626] hover:border-[#E60000]/50 transition-all hover:-translate-y-1 ${s.span}`}
              >
                <div
                  className="absolute inset-0 bg-cover bg-center opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700"
                  style={{ backgroundImage: `url('${s.img}')` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />
                <div className="relative h-full p-6 flex flex-col justify-between">
                  <div className="flex items-start justify-between gap-3">
                    <div className="w-10 h-10 grid place-items-center border border-white/20 text-[#E60000]">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="text-right">
                      <div className="font-display text-2xl text-white leading-none">{s.price}</div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-400 mt-1">{s.duration}</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-neutral-400 mb-2">{s.short}</div>
                    <h3 className="font-display text-3xl uppercase leading-none mb-2">{s.title}</h3>
                    <p className="text-sm text-neutral-300 leading-relaxed max-w-md">{s.desc}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------- WHY ----------
function Why() {
  const items = [
    { t: "We come to you", d: "Driveway, office or apartment lot — we bring power, water and pro equipment." },
    { t: "Show-car standards", d: "Two-bucket wash, microfiber-only towels, swirl-free polishing pads on every job." },
    { t: "Honest pricing", d: "Flat package pricing. No surprise upcharges, no high-pressure sales." },
    { t: "Insured & local", d: "Family-run out of Kansas City, fully insured and reviewed by hundreds of locals." },
  ];
  return (
    <section data-testid="why-section" className="py-24 sm:py-32 bg-[#0A0A0A] border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-16 items-center">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#E60000] mb-4">— Why Legacy</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight">
            Built on detail.<br/>Run on trust.
          </h2>
          <p className="mt-6 text-neutral-400 max-w-md leading-relaxed">
            Legacy started in a single garage in KC with one mission — treat every car
            like it belongs to a family member. That mindset never left.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((it, i) => (
            <div key={i} data-testid={`why-item-${i}`} className="border border-[#262626] p-6 hover:border-[#E60000]/50 transition-colors">
              <div className="font-display text-5xl text-[#E60000]/80">0{i + 1}</div>
              <div className="mt-4 font-display text-xl uppercase tracking-wide">{it.t}</div>
              <p className="mt-2 text-sm text-neutral-400 leading-relaxed">{it.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- PACKAGES ----------
function Packages() {
  return (
    <section id="packages" data-testid="packages-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-[#E60000] mb-4">— Packages</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight">
            Flat prices.<br/>Real results.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {PACKAGES.map((p) => (
            <div
              key={p.name}
              data-testid={`package-${p.name.toLowerCase()}`}
              className={`relative border p-8 flex flex-col transition-all hover:-translate-y-1 ${p.highlight ? "border-[#E60000] bg-[#0A0A0A]" : "border-[#262626] hover:border-[#404040]"}`}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-8 bg-[#E60000] text-white text-[10px] uppercase tracking-[0.3em] px-3 py-1 font-semibold">
                  {p.eyebrow}
                </div>
              )}
              {!p.highlight && (
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">{p.eyebrow}</div>
              )}
              <div className="font-display text-4xl uppercase mt-2">{p.name}</div>
              <div className="flex items-baseline gap-2 mt-4">
                <span className="font-display text-5xl text-white">{p.price}</span>
                <span className="text-xs uppercase tracking-widest text-neutral-500">{p.duration}</span>
              </div>
              <ul className="mt-8 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-neutral-300">
                    <Check className="w-4 h-4 text-[#E60000] mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`book-${p.name.toLowerCase()}-button`}
                className={`mt-8 inline-flex items-center justify-center gap-2 uppercase tracking-widest text-xs font-semibold px-6 py-4 transition-all hover:scale-[1.02] ${p.highlight ? "bg-[#E60000] hover:bg-[#FF1A1A] text-white" : "border border-white/20 hover:border-white text-white"}`}
              >
                Book {p.name} <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          ))}
        </div>
        <p className="text-center mt-8 text-xs uppercase tracking-widest text-neutral-500">
          Interior-only ($125) and Ceramic Coating ($300) also available — see services above
        </p>
      </div>
    </section>
  );
}

// ---------- GALLERY ----------
function Gallery() {
  const imgs = [
    "https://images.pexels.com/photos/31298995/pexels-photo-31298995.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://customer-assets.emergentagent.com/job_legacy-auto-detail/artifacts/wr0znufr_IMG_0186.jpg",
    "https://customer-assets.emergentagent.com/job_legacy-auto-detail/artifacts/l23ywb06_IMG_0813.jpg",
    "https://customer-assets.emergentagent.com/job_legacy-auto-detail/artifacts/0s8ycj9r_IMG_9050-2.jpeg",
    "https://images.pexels.com/photos/14908957/pexels-photo-14908957.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  ];
  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 sm:py-32 bg-[#0A0A0A] border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-[#E60000] mb-4">— Recent Work</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight">
            Garage queens & daily drivers.
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 auto-rows-[180px] md:auto-rows-[220px]">
          {imgs.map((src, i) => (
            <div
              key={i}
              data-testid={`gallery-${i}`}
              className={`relative overflow-hidden border border-[#262626] group ${i === 0 ? "col-span-2 row-span-2" : ""}`}
            >
              <img src={src} alt={`Detail ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- SERVICE AREA ----------
function Area() {
  return (
    <section id="area" data-testid="area-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-15"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1669070564746-4f33836de219?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxrYW5zYXMlMjBjaXR5JTIwc2t5bGluZSUyMGRhcmt8ZW58MHx8fHwxNzgxODI4Njk3fDA&ixlib=rb-4.1.0&q=85')",
        }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-black/70 to-[#050505]" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
        <div className="text-xs uppercase tracking-[0.3em] text-[#E60000] mb-4">— Service Area</div>
        <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tight">
          Proudly serving<br />
          <span className="text-[#E60000]">Kansas City</span> & metro.
        </h2>
        <div className="mt-12 flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
          {SERVICE_AREAS.map((c) => (
            <span
              key={c}
              data-testid={`area-${c.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              className="inline-flex items-center gap-2 border border-white/15 text-neutral-200 px-4 py-2 text-sm uppercase tracking-wide"
            >
              <MapPin className="w-3.5 h-3.5 text-[#E60000]" /> {c}
            </span>
          ))}
        </div>
        <p className="mt-8 text-sm text-neutral-400">Not sure if we cover your zip? Just ask.</p>
      </div>
    </section>
  );
}

// ---------- REVIEWS (Google) ----------
function Reviews() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [errored, setErrored] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/google-reviews`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setErrored(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Hide the section entirely if the backend has no Google Places key configured
  if (!loading && (errored || !data || !data.reviews || data.reviews.length === 0)) {
    return null;
  }

  return (
    <section id="reviews" data-testid="reviews-section" className="py-24 sm:py-32 bg-[#0A0A0A] border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#E60000] mb-4">— Google Reviews</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight">
              Real Kansas City<br/>customers.
            </h2>
          </div>
          {data && (
            <a
              href={data.google_maps_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-link"
              className="inline-flex items-center gap-3 border border-white/15 hover:border-[#E60000] transition-colors px-5 py-3"
            >
              <span className="font-display text-3xl text-white leading-none">
                {data.overall_rating?.toFixed(1) ?? "5.0"}
              </span>
              <div className="text-left">
                <div className="flex text-[#E60000] text-sm">{"★".repeat(5)}</div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-400 mt-0.5">
                  {data.user_rating_count || 0} Google reviews
                </div>
              </div>
            </a>
          )}
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-5">
            {[0,1,2].map(i => (
              <div key={i} className="border border-[#262626] p-8 h-64 animate-pulse bg-black/40" />
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-5">
            {data.reviews.slice(0, 3).map((r, i) => (
              <figure key={i} data-testid={`review-${i}`} className="border border-[#262626] p-8 hover:border-[#E60000]/50 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  {r.author_photo_url && (
                    <img src={r.author_photo_url} alt={r.author_name} className="w-10 h-10 rounded-full object-cover" referrerPolicy="no-referrer" />
                  )}
                  <div>
                    <div className="font-semibold text-white text-sm">{r.author_name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[#E60000] text-xs tracking-wider">{"★".repeat(r.rating || 5)}</span>
                      <span className="text-[10px] uppercase tracking-widest text-neutral-500">{r.relative_time}</span>
                    </div>
                  </div>
                </div>
                <blockquote className="text-neutral-200 text-sm leading-relaxed line-clamp-6">
                  {r.text}
                </blockquote>
              </figure>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ---------- CONTACT ----------
function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", vehicle: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      trackFb("Contact", { content_name: form.service || "General Inquiry" });
      toast.success("Thanks! We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", phone: "", vehicle: "", service: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please call us or try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#E60000] mb-4">— Get in touch</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight">
            Ready when<br/>you are.
          </h2>
          <p className="mt-6 text-neutral-400 max-w-md leading-relaxed">
            Book instantly through Square or drop us a note. We respond within a few hours
            during business days.
          </p>

          <div className="mt-10 space-y-5">
            {[
              { Icon: Phone, label: SITE.phone, href: `tel:${SITE.phone.replace(/[^\d+]/g, "")}`, tid: "contact-phone" },
              { Icon: Mail, label: SITE.email, href: `mailto:${SITE.email}`, tid: "contact-email" },
              { Icon: MapPin, label: SITE.address, tid: "contact-address" },
              { Icon: Clock, label: SITE.hours, tid: "contact-hours" },
            ].map(({ Icon, label, href, tid }) => (
              <div key={tid} data-testid={tid} className="flex items-center gap-4">
                <div className="w-10 h-10 grid place-items-center border border-white/15 text-[#E60000]">
                  <Icon className="w-4 h-4" />
                </div>
                {href ? (
                  <a href={href} className="text-neutral-200 hover:text-white">{label}</a>
                ) : (
                  <span className="text-neutral-200">{label}</span>
                )}
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-book-button"
              onClick={() => trackFb("Lead", { content_name: "Square Booking — Contact" })}
              className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-semibold uppercase tracking-widest text-xs px-8 py-4 transition-all hover:scale-[1.03]"
            >
              Book on Square <ChevronRight className="w-4 h-4" />
            </a>
            <a
              href={QUOTE_SMS_HREF}
              data-testid="contact-sms-quote-button"
              onClick={() => trackFb("Lead", { content_name: "SMS Quote — Contact" })}
              className="inline-flex items-center gap-2 border border-white/20 hover:border-[#E60000] hover:text-[#E60000] text-white uppercase tracking-widest text-xs px-8 py-4 transition-colors"
            >
              <MessageSquare className="w-4 h-4" /> Text for a Quote
            </a>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="border border-[#262626] p-8 bg-[#0A0A0A] space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Name" testid="field-name" value={form.name} onChange={update("name")} required />
            <Field label="Email" type="email" testid="field-email" value={form.email} onChange={update("email")} required />
            <Field label="Phone" testid="field-phone" value={form.phone} onChange={update("phone")} />
            <Field label="Vehicle (Year / Make / Model)" testid="field-vehicle" value={form.vehicle} onChange={update("vehicle")} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.3em] text-neutral-400 mb-2">Service</label>
            <select
              data-testid="field-service"
              value={form.service}
              onChange={update("service")}
              className="w-full bg-black border border-[#262626] focus:border-[#E60000] outline-none px-4 py-3 text-sm text-white"
            >
              <option value="">Select a service…</option>
              {SERVICES.map((s) => <option key={s.key} value={s.title}>{s.title}</option>)}
              <option value="Not sure">Not sure yet</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.3em] text-neutral-400 mb-2">Message</label>
            <textarea
              data-testid="field-message"
              required
              rows={5}
              value={form.message}
              onChange={update("message")}
              className="w-full bg-black border border-[#262626] focus:border-[#E60000] outline-none px-4 py-3 text-sm text-white resize-none"
              placeholder="Tell us about your vehicle and what you're looking for…"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit-button"
            className="w-full bg-[#E60000] hover:bg-[#FF1A1A] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold uppercase tracking-widest text-xs px-6 py-4 transition-all hover:scale-[1.01]"
          >
            {submitting ? "Sending…" : "Send Message"}
          </button>
        </form>
      </div>
    </section>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.3em] text-neutral-400 mb-2">{label}</label>
      <input
        data-testid={testid}
        {...props}
        className="w-full bg-black border border-[#262626] focus:border-[#E60000] outline-none px-4 py-3 text-sm text-white"
      />
    </div>
  );
}

// ---------- FOOTER ----------
function Footer() {
  return (
    <footer data-testid="site-footer" className="border-t border-white/5 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <img src={SITE.logo} alt="Legacy Auto" className="h-20 w-auto object-contain mb-4" />
          <p className="text-sm text-neutral-400 max-w-sm leading-relaxed">
            Premium mobile detailing serving Kansas City and the surrounding metro.
            We come to you — every wash, every wax, every coating.
          </p>
          <div className="flex gap-3 mt-6">
            <a href={SITE.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="w-10 h-10 grid place-items-center border border-white/15 hover:border-[#E60000] hover:text-[#E60000] transition-colors">
              <Instagram className="w-4 h-4" />
            </a>
            <a href={SITE.facebook} target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" className="w-10 h-10 grid place-items-center border border-white/15 hover:border-[#E60000] hover:text-[#E60000] transition-colors">
              <Facebook className="w-4 h-4" />
            </a>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-4">Explore</div>
          <ul className="space-y-2 text-sm text-neutral-300">
            {NAV.map((n) => (
              <li key={n.href}><a href={n.href} className="hover:text-white">{n.label}</a></li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-4">Contact</div>
          <ul className="space-y-2 text-sm text-neutral-300">
            <li>{SITE.phone}</li>
            <li>{SITE.email}</li>
            <li>{SITE.address}</li>
            <li>{SITE.hours}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/5 py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-neutral-500">
          <span>© {new Date().getFullYear()} Legacy Auto Detailing. All rights reserved.</span>
          <span className="uppercase tracking-widest">Kansas City, MO · KS</span>
        </div>
      </div>
    </footer>
  );
}

// ---------- APP ----------
function App() {
  return (
    <div className="App">
      <Toaster theme="dark" position="top-right" richColors />
      <Header />
      <Hero />
      <Marquee />
      <Services />
      <Why />
      <Packages />
      <Gallery />
      <Area />
      <Reviews />
      <Contact />
      <Footer />
    </div>
  );
}

export default App;
